import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripe";

function EdClaimFeeForm({ clientSecret, feePaymentId, amountCents, onAuthorized, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const authorizeHold = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    onError("");
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        onError(error.message || "Payment authorization failed.");
        return;
      }
      if (paymentIntent?.status === "requires_capture") {
        const syncFn = firebase.functions().httpsCallable("syncEdClaimFeePayment");
        await syncFn({ feePaymentId });
        onAuthorized(feePaymentId);
        return;
      }
      onError("Card hold was not authorized. Please try again.");
    } catch (err) {
      console.warn("[EdClaimFeeForm] authorize failed:", err);
      onError(err?.message || "Payment authorization failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A temporary hold of ${(amountCents / 100).toFixed(2)} will be placed on your card. You are
        only charged if an admin approves your claim.
      </Typography>
      {clientSecret ? <PaymentElement /> : null}
      <Button
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
        onClick={authorizeHold}
        disabled={loading || !stripe || !clientSecret}
      >
        {loading ? "Authorizing…" : "Authorize card hold"}
      </Button>
    </Box>
  );
}

/**
 * Multi-step ED claim panel for unclaimed locations.
 */
export default function EdClaimPanel({ location, locationId, onSubmitted }) {
  const proposedDomain = location?.facilityDomain?.proposed || null;
  const [activeStep, setActiveStep] = useState(0);
  const [npi, setNpi] = useState("");
  const [domainEmail, setDomainEmail] = useState("");
  const [domainVerificationId, setDomainVerificationId] = useState(null);
  const [domainVerified, setDomainVerified] = useState(false);
  const [domainSendStatus, setDomainSendStatus] = useState(null);
  const [shareGeo, setShareGeo] = useState(false);
  const [feeConfig, setFeeConfig] = useState({ feeCents: 0, enabled: false });
  const [feePaymentId, setFeePaymentId] = useState(null);
  const [feeClientSecret, setFeeClientSecret] = useState(null);
  const [feeAuthorized, setFeeAuthorized] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    try {
      const stored = sessionStorage.getItem(`ed_claim_domain_ver_${locationId}`);
      if (stored) {
        setDomainVerificationId(stored);
        setDomainVerified(true);
      }
    } catch (err) {
      console.warn("[EdClaimPanel] sessionStorage read failed:", err);
    }
  }, [locationId]);

  useEffect(() => {
    let cancelled = false;
    firebase
      .functions()
      .httpsCallable("getEdClaimFeeConfig")()
      .then((res) => {
        if (!cancelled) setFeeConfig(res.data || { feeCents: 0, enabled: false });
      })
      .catch((err) => {
        console.warn("[EdClaimPanel] fee config failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSteps = useMemo(() => {
    const steps = ["NPI"];
    if (proposedDomain) steps.push("Email domain");
    steps.push("Location signal");
    if (feeConfig.enabled) steps.push("Fee");
    steps.push("Submit");
    return steps;
  }, [proposedDomain, feeConfig.enabled]);

  const sendDomainVerification = async () => {
    setDomainSendStatus("loading");
    setError("");
    try {
      const fn = firebase.functions().httpsCallable("sendEdClaimDomainVerificationEmail");
      const res = await fn({ locationId, email: domainEmail.trim() });
      setDomainVerificationId(res.data?.verificationId || null);
      setDomainSendStatus("sent");
    } catch (err) {
      console.warn("[EdClaimPanel] domain email send failed:", err);
      setDomainSendStatus("error");
      setError(err?.message || "Could not send verification email.");
    }
  };

  const initFeeAuthorization = useCallback(async () => {
    if (!feeConfig.enabled) return;
    try {
      const fn = firebase.functions().httpsCallable("authorizeEdClaimFeePayment");
      const res = await fn({ locationId });
      setFeePaymentId(res.data?.feePaymentId || null);
      setFeeClientSecret(res.data?.clientSecret || null);
      if (res.data?.status === "authorized") {
        setFeeAuthorized(true);
        setFeePaymentId(res.data.feePaymentId);
      }
    } catch (err) {
      console.warn("[EdClaimPanel] fee authorize init failed:", err);
      setError(err?.message || "Could not start fee authorization.");
    }
  }, [feeConfig.enabled, locationId]);

  useEffect(() => {
    if (visibleSteps[activeStep] === "Fee" && feeConfig.enabled && !feeClientSecret) {
      initFeeAuthorization();
    }
  }, [activeStep, visibleSteps, feeConfig.enabled, feeClientSecret, initFeeAuthorization]);

  const readGeolocation = () =>
    new Promise((resolve) => {
      if (!shareGeo || !navigator.geolocation) {
        resolve({ lat: null, lng: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve({ lat: null, lng: null }),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    });

  const submitClaim = async () => {
    setSubmitLoading(true);
    setError("");
    try {
      const geo = await readGeolocation();
      const fn = firebase.functions().httpsCallable("submitEdLocationClaim");
      await fn({
        locationId,
        npi: npi.trim(),
        domainVerificationId: domainVerificationId || undefined,
        feePaymentId: feeAuthorized ? feePaymentId : undefined,
        claimantLat: geo.lat,
        claimantLng: geo.lng,
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      console.warn("[EdClaimPanel] submit failed:", err);
      setError(err?.message || "Could not submit claim.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const canAdvanceFromNpi = npi.replace(/\D/g, "").length === 10;
  const canAdvanceFromDomain = !proposedDomain || domainVerified;
  const canAdvanceFromFee = !feeConfig.enabled || feeAuthorized;

  const goNext = () => {
    const current = visibleSteps[activeStep];
    if (current === "NPI" && !canAdvanceFromNpi) return;
    if (current === "Email domain" && proposedDomain && !domainVerified) {
      setError("Complete email verification before continuing.");
      return;
    }
    if (current === "Fee" && !canAdvanceFromFee) {
      setError("Authorize the card hold before continuing.");
      return;
    }
    setError("");
    setActiveStep((s) => Math.min(s + 1, visibleSteps.length - 1));
  };

  const goBack = () => {
    setError("");
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  if (success) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Claim submitted — pending admin approval. You will be notified when reviewed.
      </Alert>
    );
  }

  const currentStepName = visibleSteps[activeStep];

  return (
    <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Claim this emergency department
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Physicians: complete verification steps below. An admin must approve before you are linked
        as supervisor.
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {visibleSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {currentStepName === "NPI" ? (
        <Box>
          <TextField
            size="small"
            fullWidth
            label="NPI (10 digits)"
            value={npi}
            onChange={(e) => setNpi(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Must be an active individual physician (Type 1) NPI.
          </Typography>
        </Box>
      ) : null}

      {currentStepName === "Email domain" && proposedDomain ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Confirm an email at <strong>@{proposedDomain}</strong> (not shown publicly).
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={`Email @${proposedDomain}`}
            value={domainEmail}
            onChange={(e) => setDomainEmail(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={sendDomainVerification}
            disabled={domainSendStatus === "loading" || !domainEmail.includes("@")}
          >
            {domainSendStatus === "loading" ? "Sending…" : "Send verification link"}
          </Button>
          {domainSendStatus === "sent" && !domainVerified ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Check your inbox and click the verification link, then return here to continue.
            </Alert>
          ) : null}
          {domainVerified ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Email verified at @{proposedDomain}
            </Alert>
          ) : null}
        </Box>
      ) : null}

      {currentStepName === "Location signal" ? (
        <Box>
          <FormControlLabel
            control={
              <Checkbox checked={shareGeo} onChange={(e) => setShareGeo(e.target.checked)} />
            }
            label="Share my current location with reviewers (optional, never blocks approval)"
          />
          <Typography variant="caption" display="block" color="text.secondary">
            Only a distance estimate is stored — not your ongoing location.
          </Typography>
        </Box>
      ) : null}

      {currentStepName === "Fee" && feeConfig.enabled ? (
        <Box>
          {feeClientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret: feeClientSecret }}>
              <EdClaimFeeForm
                clientSecret={feeClientSecret}
                feePaymentId={feePaymentId}
                amountCents={feeConfig.feeCents}
                onAuthorized={(id) => {
                  setFeePaymentId(id);
                  setFeeAuthorized(true);
                  setError("");
                }}
                onError={setError}
              />
            </Elements>
          ) : (
            <Typography variant="body2">Loading payment form…</Typography>
          )}
        </Box>
      ) : null}

      {currentStepName === "Submit" ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Review and submit your claim for admin approval.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            NPI: {npi.replace(/\D/g, "")}
          </Typography>
          {proposedDomain ? (
            <Typography variant="body2" color="text.secondary">
              Domain: {domainVerified || domainVerificationId ? `verified @${proposedDomain}` : "pending"}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Domain: not on file — flagged for admin review
            </Typography>
          )}
          {feeConfig.enabled ? (
            <Typography variant="body2" color="text.secondary">
              Fee hold: {feeAuthorized ? "authorized" : "not authorized"}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        {activeStep > 0 ? (
          <Button size="small" onClick={goBack} disabled={submitLoading}>
            Back
          </Button>
        ) : null}
        {currentStepName !== "Submit" ? (
          <Button
            size="small"
            variant="contained"
            onClick={goNext}
            disabled={
              (currentStepName === "NPI" && !canAdvanceFromNpi) ||
              (currentStepName === "Email domain" && !canAdvanceFromDomain)
            }
          >
            Next
          </Button>
        ) : (
          <Button size="small" variant="contained" onClick={submitClaim} disabled={submitLoading}>
            {submitLoading ? "Submitting…" : "Submit claim"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Whether a location can show the claim CTA.
 * @param {object | null | undefined} location
 * @param {boolean} isLoggedIn
 */
export function canShowEdClaimPanel(location, isLoggedIn) {
  if (!isLoggedIn || !location) return false;
  const users = location.users;
  if (Array.isArray(users) && users.length > 0) return false;
  if (location.source === "cms_import") return true;
  return !location.source || location.source === "legacy";
}
